import { RemovalModal } from '@app/components/removal-modal';
import { ColsWithoutTable } from '@app/components/rubber-table/blocks/cols-without-table';
import AccordionItemUI from '@app/components/ui/Accordion/AccordionItem';
import { usePermissionCheck } from '@app/hooks/use-permission';
import { AllStatusProject } from '@app/interfaces/analytics';
import { GPTGroupObjectType, IndividualGptMetricType } from '@app/interfaces/gpt-metric.type';
import { AccordionItemUIPropsClickChildParams } from '@app/interfaces/userInterface';
import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';
import {
  useDeleteChatGptGroupMutation,
  useGetChatGptGroupsWithMetricsQuery,
  useLazyExportGptChatGroupQuery,
} from '@app/store/api/chat-api/gpt-metric-chat.api';
import {
  useDeleteGptGroupMutation,
  useGetGptGroupsWithMetricsQuery,
  useLazyExportGptGroupQuery,
} from '@app/store/api/gpt-metric.api';
import { userPermissions } from '@app/utils/user-permissions';
import { Button, CircleButton, EasyModal } from '@ui';
import React, { FC, useState } from 'react';
import { useOutletContext, useParams } from 'react-router';

import { GPTAllIntegrationListObjectType } from '@app/interfaces/settings.type';

import { useTranslation } from '@app/hooks/use-translation';

import ImportGptGroupContent from './import-gpt-group-content';
type GptMetricGroupsPropsType = {
  circleButtonClick: (metric_id: string, type: string, copy?: boolean) => void;
  currentTemplate: 'chat' | 'voice';
  gptModelsList: GPTAllIntegrationListObjectType | undefined;
  disableFunc: boolean;
};
const GptMetricGroups: FC<GptMetricGroupsPropsType> = (props) => {
  const { circleButtonClick, currentTemplate, gptModelsList, disableFunc } = props;
  const { t } = useTranslation('pages.metricTemplates');
  const { id } = useParams();
  const { status } = useOutletContext<AnalyticPageOutlet>();
  const project_id = id || '';
  const gptGptGroupMetricsAccess = usePermissionCheck({
    permissions: 'action',
    tag: userPermissions.action.actionGetGPTGroupMetrics,
  });
  const { data: gptGroupsListVoice } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !!gptGptGroupMetricsAccess || !project_id || currentTemplate === 'chat' },
  );
  const { data: gptGroupsListChat } = useGetChatGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !!gptGptGroupMetricsAccess || !project_id || currentTemplate === 'voice' },
  );
  const gptGroupsList =
    { chat: gptGroupsListChat, voice: gptGroupsListVoice }?.[currentTemplate] || undefined;
  const [deleteGroupVoice] = useDeleteGptGroupMutation();
  const [deleteGroupChat] = useDeleteChatGptGroupMutation();
  const deleteGroup = { chat: deleteGroupChat, voice: deleteGroupVoice }[currentTemplate];
  const [groupIdForDelete, setGroupIdForDelete] = useState<string>();

  const [exportGPTGroupVoice] = useLazyExportGptGroupQuery();
  const [exportGPTGroupChat] = useLazyExportGptChatGroupQuery();
  const exportGPTGroup = { chat: exportGPTGroupChat, voice: exportGPTGroupVoice }[currentTemplate];

  function handleExportGroup(gpt_group_id: string) {
    exportGPTGroup({ gpt_group_id });
  }

  function handleDeleteGroup() {
    if (!groupIdForDelete) return;
    deleteGroup({ gpt_group_id: groupIdForDelete });
  }

  function getLabel(
    group: GPTGroupObjectType & {
      gpt_metrics: ({
        metric_id: string;
      } & IndividualGptMetricType)[];
    },
  ) {
    return (
      <div className="grid grid-rows-1 grid-cols-[calc(100%-250px)_120px] justify-between w-[calc(100%-30px)]">
        <div className="flex items-center gap-[6px] w-full ">
          <div className="text-1color text-[16px] font-bold leading-[187%] tracking-[-0.03em] w-fit truncate">
            {group.name}
          </div>
          <div className="flex font-[400] justify-center items-center text-xs w-[20px] h-[20px] rounded-full ml-3 bg-defaultBgBtn text-white">
            {group.gpt_metrics.length}
          </div>
          {typeof group.estimated_duration_max === 'number' && (
            <div className="p-1.5 flex items-center rounded-lg justify-center text-[12px] min-w-fit text-black font-medium bg-basic_app_bg">
              до {group.estimated_duration_max} {currentTemplate === 'chat' ? 'слов' : 'мин'}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-[6px] danger">
          <CircleButton
            onClick={() => circleButtonClick(group.gpt_group_id, 'gpt')}
            size={19}
            hintTitle={t('popup_hints.settings_button')}
            icon="SettingIcon"
            className=" text-3color flex items-center"
          />
          <CircleButton
            onClick={() => handleExportGroup(group.gpt_group_id)}
            size={17}
            icon="DownloadArrowIcon"
            hintTitle={t('popup_hints.download_button')}
            className=" text-3color flex items-center"
          />
          <CircleButton
            onClick={() => {
              circleButtonClick(group.gpt_group_id, 'gpt', true);
            }}
            icon="CopyIcon"
            size={18}
            className=" text-action"
            hintTitle={t('popup_hints.copy_button')}
          />
          <CircleButton
            onClick={() => setGroupIdForDelete(group.gpt_group_id)}
            hintTitle={t('popup_hints.delete_button')}
            size={17.5}
            icon="TrashIcon"
            className=" text-basic_red"
            disable={status?.project_status === AllStatusProject.ACTIVE}
          />
        </div>
      </div>
    );
  }

  // ACCORDION
  const [openedAccordion, changeAccordionState] = useState<string[]>([]);

  function handleChangeAccordionState({ id }: AccordionItemUIPropsClickChildParams) {
    changeAccordionState((prev) => {
      return prev.includes(id) ? prev.filter((ids) => ids !== id) : [...prev, id];
    });
  }
  // ACCORDION
  const [importModalState, setImportModalState] = useState(false);

  function handleOpenImportModal() {
    setImportModalState(true);
  }
  function onCloseImportModal() {
    setImportModalState(false);
  }
  return (
    <>
      <div className="w-full h-full">
        <div className="flex items-center justify-between">
          <h2 className="text-[24px] font-bold text-1color py-[24px]">GPT-метрики</h2>
          <Button
            label="Импорт группы GPT-метрик"
            onClick={handleOpenImportModal}
            disabled={disableFunc}
          />
        </div>
        <ColsWithoutTable
          columns={[
            { index: 'name', title: 'Название', maxWidth: 240 },
            { index: 'format', title: 'Формат ответа', maxWidth: 170 },
            { index: 'validResponse', title: 'Валидация ответа', maxWidth: 280 },
            { index: 'settings', title: 'Настройки метрики' },
            { index: 'actions', title: 'Действия', maxWidth: 110 },
          ]}
        />
        {gptGroupsList?.gpt_groups.map((group) => {
          const label = getLabel(group);
          const isActive = openedAccordion.includes(group.gpt_group_id);
          return (
            <AccordionItemUI
              variant="filled"
              active={isActive}
              title={label}
              id={group.gpt_group_id}
              key={group.gpt_group_id}
              bottomWithoutBorder
              clickBtnChild={handleChangeAccordionState}
            >
              <>
                {group.gpt_metrics.map((metric, index) => (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '220px 150px 300px 1fr 120px',
                    }}
                    className="grid  relative  grid-rows-1 py-4 px-3 bg-white last:rounded-b-[12px] before:border-b-solid last:border-b-3color before:border-b-[1px] before:content-[''] before:absolute before:bottom-0 before:w-[calc(100%-24px)] before:left-[12px] before:my-[1px] last:before:hidden hover:bg-action/[.05]"
                    key={metric.metric_id + index}
                  >
                    <div>{metric.name}</div>
                    <div className="text-center">
                      {metric.result_value_type === 'num'
                        ? 'Число'
                        : metric.settings.str_validation === 'text'
                        ? 'Строка'
                        : 'Текст'}
                    </div>
                    <div>
                      {metric.settings.validation?.value?.join(
                        `${metric.settings.validation?.type === 'array' ? ',' : '-'}`,
                      )}
                    </div>
                    <div className="p-[4px_8px] col-span-2 rounded-[12px] bg-defaultBg flex w-full items-center truncate">
                      {'“'}
                      <span className=" truncate ">{metric.settings?.prompt}</span>
                      {'”'}
                    </div>
                  </div>
                ))}
              </>
            </AccordionItemUI>
          );
        })}
      </div>
      <RemovalModal
        onClose={() => setGroupIdForDelete(undefined)}
        show={!!groupIdForDelete}
        handleDelete={handleDeleteGroup}
        description={'Вы уверены что хотите удалить данную группу GPT метрик?'}
      />
      <EasyModal show={importModalState} withoutFooter onClose={onCloseImportModal}>
        {importModalState && (
          <ImportGptGroupContent
            currentTemplate={currentTemplate}
            circleButtonClick={circleButtonClick}
            gptModelsList={gptModelsList}
            setImportModalState={setImportModalState}
            project_id={project_id}
          />
        )}
      </EasyModal>
    </>
  );
};

export default GptMetricGroups;
